// The compact edit experience for a dev plan item: create or edit its fixed
// fields, move its status, and set what it depends on. Three tabs over three
// independent write handlers, because each already carries its own rules
// (upsertDevPlanItem never touches status; setDevPlanStatus requires a reason
// or a real completion date; setDevPlanDependencies refuses an unknown ref)
// and folding them into one save would mean re-deciding which rule applies to
// which field on every click.
//
// Only rendered at all when the caller has already checked can_edit from
// getDevPlan's own response; nothing in here re-derives that.
import { useEffect, useState } from 'react'
import { Badge } from '../../lib/shadcn/badge'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../lib/shadcn/tabs'
import { formatDate, statusLabel, type GanttItem } from '../../utils/devPlan'

export type DevPlanEditTarget = { mode: 'edit'; item: GanttItem } | { mode: 'create' }

type Assignee = { person_id: string | null; display_name: string | null; email: string }
type Phase = { phase_key: string; label: string }

export function DevPlanEditPanel({
  target,
  phases,
  statuses,
  assignees,
  allItems,
  busy,
  onSaveDetails,
  onSaveStatus,
  onSaveDependencies,
  onClose,
}: {
  target: DevPlanEditTarget
  phases: Phase[]
  statuses: string[]
  assignees: Assignee[]
  allItems: GanttItem[]
  busy: boolean
  onSaveDetails: (p: {
    ref: string
    phase_key: string
    title: string
    planned_start: string | null
    planned_end: string | null
    owner_person_id: string | null
  }) => void
  onSaveStatus: (p: {
    ref: string
    status: string
    actual_completion: string | null
    reason: string | null
  }) => void
  onSaveDependencies: (p: { ref: string; depends_on_refs: string[] }) => void
  onClose: () => void
}) {
  const isNew = target.mode === 'create'
  const item = target.mode === 'edit' ? target.item : null
  const key = isNew ? '__new__' : item!.ref

  // Details
  const [ref, setRef] = useState('')
  const [title, setTitle] = useState('')
  const [phaseKey, setPhaseKey] = useState(phases[0]?.phase_key ?? '')
  const [plannedStart, setPlannedStart] = useState('')
  const [plannedEnd, setPlannedEnd] = useState('')
  const [ownerPersonId, setOwnerPersonId] = useState('')

  // Status
  const [status, setStatus] = useState('in_progress')
  const [completed, setCompleted] = useState('')
  const [reason, setReason] = useState('')

  // Dependencies
  const [dependsOn, setDependsOn] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (isNew) {
      setRef('')
      setTitle('')
      setPhaseKey(phases[0]?.phase_key ?? '')
      setPlannedStart('')
      setPlannedEnd('')
      setOwnerPersonId('')
      setDependsOn(new Set())
      return
    }
    const i = item!
    setRef(i.ref)
    setTitle(i.title)
    setPhaseKey(i.phase_key)
    setPlannedStart(i.planned_start ?? '')
    setPlannedEnd(i.planned_end ?? '')
    setOwnerPersonId(i.owner_person_id ?? '')
    setStatus(i.status)
    setCompleted(i.actual_completion ?? '')
    setReason(i.blocked_reason ?? '')
    const byId = new Map(allItems.map((x) => [x.item_id, x.ref]))
    setDependsOn(new Set(i.depends_on_ids.map((id) => byId.get(id)).filter((r): r is string => !!r)))
    // Reset whenever the selected item (or switch to create) changes, not on
    // every keystroke: allItems and phases are stable references from the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  function saveDetails() {
    onSaveDetails({
      ref: ref.trim(),
      phase_key: phaseKey,
      title: title.trim(),
      planned_start: plannedStart || null,
      planned_end: plannedEnd || null,
      owner_person_id: ownerPersonId || null,
    })
  }

  function saveStatus() {
    if (!item) return
    onSaveStatus({
      ref: item.ref,
      status,
      actual_completion: completed || null,
      reason: reason || null,
    })
  }

  function saveDependencies() {
    if (!item) return
    onSaveDependencies({ ref: item.ref, depends_on_refs: [...dependsOn] })
  }

  const otherItems = allItems.filter((i) => i.ref !== ref)
  const assignableOwners = assignees.filter((a): a is Assignee & { person_id: string } => !!a.person_id)
  const detailsDisabled = !ref.trim() || !phaseKey || !title.trim()

  return (
    <section className="rounded-md border border-border bg-background p-3">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {isNew ? 'New plan item' : `Update ${item!.ref}`}
        </h2>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="status" disabled={isNew}>
            Status
          </TabsTrigger>
          <TabsTrigger value="dependencies" disabled={isNew}>
            Depends on
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-2 pt-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Ref</Label>
              <Input
                value={ref}
                disabled={!isNew}
                onChange={(e) => setRef(e.target.value)}
                placeholder="e.g. 1.5.11"
              />
            </div>
            <div>
              <Label className="text-xs">Phase</Label>
              <NativeSelect value={phaseKey} onChange={(e) => setPhaseKey(e.target.value)}>
                {phases.map((p) => (
                  <option key={p.phase_key} value={p.phase_key}>
                    {p.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <div>
              <Label className="text-xs">Planned start</Label>
              <Input type="date" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Planned end</Label>
              <Input type="date" value={plannedEnd} onChange={(e) => setPlannedEnd(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Owner</Label>
              <NativeSelect value={ownerPersonId} onChange={(e) => setOwnerPersonId(e.target.value)}>
                <option value="">Unassigned</option>
                {assignableOwners.map((a) => (
                  <option key={a.person_id} value={a.person_id}>
                    {a.display_name ?? a.email}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
          <Button type="button" size="sm" disabled={busy || detailsDisabled} onClick={saveDetails}>
            {isNew ? 'Create' : 'Save details'}
          </Button>
        </TabsContent>

        <TabsContent value="status" className="space-y-2 pt-2">
          {item && (
            <>
              <div className="grid gap-2 sm:grid-cols-3">
                <div>
                  <Label className="text-xs">Status</Label>
                  <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)}>
                    {statuses.map((s) => (
                      <option key={s} value={s}>
                        {statusLabel(s)}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
                <div>
                  <Label className="text-xs">
                    {status === 'done' ? 'Date it was actually finished' : 'Finished on'}
                  </Label>
                  <Input type="date" value={completed} onChange={(e) => setCompleted(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Reason, if blocked or on hold</Label>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
              </div>
              {status === 'done' && (
                <p className="text-[11px] text-muted-foreground">
                  Give the real date. Ticking today for something finished last week puts an error
                  into every cycle-time figure this plan is measured from.
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">
                Currently planned {formatDate(item.planned_start)} to {formatDate(item.planned_end)}.
              </p>
              <Button type="button" size="sm" disabled={busy} onClick={saveStatus}>
                Save status
              </Button>
            </>
          )}
        </TabsContent>

        <TabsContent value="dependencies" className="space-y-2 pt-2">
          {item && (
            <>
              <p className="text-[11px] text-muted-foreground">
                What {item.ref} depends on. Saving refuses the whole set if any ref chosen here is
                not on record, so this list can never show a dependency that was not actually
                written.
              </p>
              {otherItems.length === 0 && (
                <p className="text-sm text-muted-foreground">No other plan items to depend on.</p>
              )}
              <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                {otherItems.map((i) => (
                  <li key={i.item_id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      id={`dep-${i.ref}`}
                      checked={dependsOn.has(i.ref)}
                      onCheckedChange={(checked) => {
                        setDependsOn((prev) => {
                          const next = new Set(prev)
                          if (checked === true) next.add(i.ref)
                          else next.delete(i.ref)
                          return next
                        })
                      }}
                    />
                    <label htmlFor={`dep-${i.ref}`} className="min-w-0 flex-1 truncate">
                      <span className="font-medium tabular-nums">{i.ref}</span>{' '}
                      <span className="text-muted-foreground">{i.title}</span>
                    </label>
                    <Badge variant="outline" className="shrink-0">
                      {statusLabel(i.status)}
                    </Badge>
                  </li>
                ))}
              </ul>
              <Button type="button" size="sm" disabled={busy} onClick={saveDependencies}>
                Save dependencies
              </Button>
            </>
          )}
        </TabsContent>
      </Tabs>
    </section>
  )
}
